import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="verification-refresh-poll"
// While a background verification refresh is running (marked by a
// [data-refresh-active] element), count the status label down to when the refresh
// is due — refreshes are rate-limited, so it can be up to a minute out — then poll
// the show page and swap in fresh checklist content every `interval` ms, up to
// `max` times or until the refresh finishes.
//
// Same-URL reloads morph the page (turbo_refreshes_with :morph), which keeps this
// controller alive without re-running connect(), so #start is also wired to
// turbo:morph from the view.
export default class extends Controller {
  static targets = ["status"];
  static values = {
    url: String,
    delay: { type: Number, default: 0 },
    interval: { type: Number, default: 5000 },
    max: { type: Number, default: 5 },
  };

  connect() {
    this.start();
  }

  disconnect() {
    this.clearTimer();
  }

  start() {
    this.clearTimer();
    if (!this.refreshing()) return;
    this.attempts = 0;
    this.remaining = this.delayValue;
    this.countdown();
  }

  refreshing() {
    return this.element.querySelector("[data-refresh-active]") !== null;
  }

  // Count the seconds remaining down to the scheduled run (the bare number — the
  // unit is implied), then start polling.
  countdown() {
    if (this.remaining > 0) {
      this.setStatus(`${Math.ceil(this.remaining / 1000)}`);
      this.timer = setTimeout(() => {
        this.remaining -= 1000;
        this.countdown();
      }, 1000);
    } else {
      this.setStatus("Refreshing…");
      this.schedulePoll();
    }
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text;
  }

  schedulePoll() {
    this.clearTimer();
    this.timer = setTimeout(() => this.poll(), this.intervalValue);
  }

  async poll() {
    this.attempts += 1;
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/html" },
      });
      const doc = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const fresh = doc.getElementById(this.element.id);
      if (fresh) this.element.innerHTML = fresh.innerHTML;
    } catch {
      // Network hiccup — leave the current content and stop polling.
      return;
    }
    if (this.refreshing() && this.attempts < this.maxValue) this.schedulePoll();
  }

  clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
